import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { QuickMathStepPayload } from '../../../../constants/bonusConstants';
import { QuickMathStepDisplay, type QuickMathStepView } from './QuickMathStepDisplay';
import { FlipRollingInteger } from '../../../shared/FlipRollingInteger';
import './quick-math.css';
import { useTranslation } from '../../../../i18n';

/** How long success/error line stays visible below Submit (ms). */
const QUICK_MATH_FLASH_MS = 1400;

export interface BonusAnswerAckQuickMath {
  lastStepCorrect: boolean;
  stepPoints: number;
  done: boolean;
  step?: QuickMathStepView;
}

function randomOperand(): number {
  return 1 + Math.floor(Math.random() * 20);
}

/** Digits only — answers stay non-negative (generator keeps every step in 0–50). */
function sanitizeQuickMathInput(raw: string): string {
  return raw.replace(/\D/g, '');
}

interface QuickMathPlayerProps {
  roomName: string;
  questionId: string;
  steps: QuickMathStepPayload[];
  disabled: boolean;
  /** Wall-clock ms when bonus play starts (after intro). */
  bonusIntroEndsAt: number;
  /** When host is playing: seconds left in intro (synced with timer bar); show a visible countdown for the room. */
  introSecondsLeft?: number | null;
  /** Fired once when server ack says the full chain is solved (parent shows praise overlay). */
  onChainComplete?: () => void;
  socket: {
    emit: (event: string, data: unknown) => void;
    on: (event: string, cb: (data: unknown) => void) => void;
    off: (event: string, cb?: (data: unknown) => void) => void;
  };
}

function firstStepView(s: QuickMathStepPayload | undefined): QuickMathStepView | null {
  if (!s) return null;
  return { accumulator: s.accumulator, op: s.op, operand: s.operand };
}

/**
 * Timer-based quick-math: many bonus-answer submits per round; server ack advances step.
 * During bonus intro countdown, first two operands roll like standings scores until play starts.
 */
export const QuickMathPlayer: React.FC<QuickMathPlayerProps> = ({
  roomName,
  questionId,
  steps,
  disabled,
  bonusIntroEndsAt,
  introSecondsLeft = null,
  onChainComplete,
  socket,
}) => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState<QuickMathStepView | null>(() => firstStepView(steps[0]));
  const [input, setInput] = useState('');
  const [flash, setFlash] = useState<'ok' | 'bad' | null>(null);
  const [chainDone, setChainDone] = useState(false);
  const busyRef = useRef(false);
  const questionIdRef = useRef(questionId);
  const chainCompleteNotifiedRef = useRef(false);
  const [now, setNow] = useState(() => Date.now());

  /** Rolling state during intro: prev* drives FlipRollingInteger "from". */
  const [roll, setRoll] = useState(() => ({
    prevA: undefined as number | undefined,
    prevB: undefined as number | undefined,
    a: randomOperand(),
    b: randomOperand(),
  }));

  const inIntroCountdown = !disabled && now < bonusIntroEndsAt;

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (questionIdRef.current !== questionId) {
      questionIdRef.current = questionId;
      setCurrent(firstStepView(steps[0]));
      setInput('');
      setFlash(null);
      setChainDone(false);
      busyRef.current = false;
      chainCompleteNotifiedRef.current = false;
      setRoll({
        prevA: undefined,
        prevB: undefined,
        a: randomOperand(),
        b: randomOperand(),
      });
    }
  }, [questionId, steps]);

  useEffect(() => {
    if (!inIntroCountdown) return;
    const id = window.setInterval(() => {
      setRoll((x) => ({
        prevA: x.a,
        prevB: x.b,
        a: randomOperand(),
        b: randomOperand(),
      }));
    }, 220);
    return () => window.clearInterval(id);
  }, [inIntroCountdown]);

  useEffect(() => {
    if (disabled) busyRef.current = false;
  }, [disabled]);

  useEffect(() => {
    const onAck = (data: unknown) => {
      const d = data as {
        points?: number;
        quickMath?: BonusAnswerAckQuickMath;
      };
      if (!d?.quickMath) return;
      busyRef.current = false;
      const q = d.quickMath;
      setFlash(q.lastStepCorrect ? 'ok' : 'bad');
      window.setTimeout(() => setFlash(null), QUICK_MATH_FLASH_MS);
      setInput('');
      if (q.done) {
        setChainDone(true);
        setCurrent(null);
        if (!chainCompleteNotifiedRef.current) {
          chainCompleteNotifiedRef.current = true;
          onChainComplete?.();
        }
      } else if (q.step) {
        setCurrent(q.step);
      }
    };
    socket.on('bonus-answer-ack', onAck);
    return () => {
      socket.off('bonus-answer-ack', onAck);
    };
  }, [socket, onChainComplete]);

  const submit = useCallback(() => {
    if (disabled || chainDone || !current || busyRef.current) return;
    const trimmed = input.trim();
    if (!trimmed || !/^\d+$/.test(trimmed)) return;
    busyRef.current = true;
    socket.emit('bonus-answer', { roomName, answer: trimmed });
  }, [disabled, chainDone, current, input, roomName, socket]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  const playDisabled = disabled || chainDone || !current || inIntroCountdown;
  const canSubmit = /^\d+$/.test(input.trim());

  if (inIntroCountdown) {
    const showIntroCounter = introSecondsLeft != null && introSecondsLeft > 0;
    return (
      <div style={{ width: '100%', maxWidth: '100%', padding: '0 0.5rem', boxSizing: 'border-box' }}>
        {showIntroCounter ? (
          <div className="quick-math-intro-counter" role="status" aria-live="polite">
            {t('bonus.player.startingIn', { seconds: introSecondsLeft })}
          </div>
        ) : null}
        <div className="quick-math-step" aria-live="polite">
          <FlipRollingInteger
            className="quick-math-step__acc"
            style={{ fontSize: 'clamp(1.75rem, 8vw, 2.75rem)', fontWeight: 800 }}
            from={roll.prevA}
            to={roll.a}
          />
          <span className="quick-math-step__op">+</span>
          <FlipRollingInteger
            className="quick-math-step__operand"
            style={{ fontSize: 'clamp(1.75rem, 8vw, 2.75rem)', fontWeight: 800 }}
            from={roll.prevB}
            to={roll.b}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: '100%', padding: '0 0.5rem', boxSizing: 'border-box' }}>
      <QuickMathStepDisplay
        step={current}
        emptyLabel={chainDone ? t('bonus.player.quickMathDone') : '—'}
      />
      {!chainDone && current ? (
        <div className="quick-math-input-row">
          <input
            className="quick-math-input"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder={t('bonus.player.quickMathPlaceholder')}
            value={input}
            disabled={playDisabled}
            onChange={(e) => setInput(sanitizeQuickMathInput(e.target.value))}
            onKeyDown={onKeyDown}
            aria-label={t('bonus.player.quickMathAnswerAria')}
          />
          <button
            type="button"
            className="quick-math-submit"
            disabled={playDisabled || !canSubmit}
            onClick={submit}
          >
            {t('bonus.player.quickMathSubmit')}
          </button>
        </div>
      ) : null}
      <div
        className={
          flash === 'ok'
            ? 'quick-math-flash quick-math-flash--ok'
            : flash === 'bad'
              ? 'quick-math-flash quick-math-flash--bad'
              : 'quick-math-flash'
        }
      >
        {flash === 'ok' ? t('bonus.player.quickMathFlashOk') : flash === 'bad' ? t('bonus.player.quickMathFlashBad') : '\u00a0'}
      </div>
    </div>
  );
};
